import React, { useState, useEffect, useMemo, useRef } from 'react';
import Footer from '../component/Footer';
import baseURL from '../component/baseURL';
import showNotification from '../component/Notification';
import { useNavigate } from 'react-router-dom';
import stringsoflanguages from '../component/stringsoflanguages';

/** API often returns `code` / `result_code` as number or string */
function isServerSuccess(responseJson) {
    const s = responseJson?.server;
    if (!s) return false;
    const code = Number(s.code);
    const rc = Number(s.result_code);
    return (
        code === 200 ||
        rc === 200 ||
        s.code === '200' ||
        s.result_code === '200'
    );
}

function getFieldLabel(f) {
    if (!f || typeof f !== 'object') return '';
    const label = (f.field_name_label || f.field_name || '').trim();
    return label || '—';
}

function isFieldRequired(field) {
    return field?.field_required === 1 || field?.field_required === '1';
}

function isFieldDefault(field) {
    return field?.is_default_field === 1 || field?.is_default_field === '1';
}

function isFieldLocked(field) {
    return isFieldRequired(field) || isFieldDefault(field);
}

/** Insert index in 0..n from pointer Y over `[data-listing-reorder-id]` nodes (top half of row i ⇒ insert before i). */
function getReorderInsertBeforeIndex(containerEl, clientY) {
    if (!containerEl) return 0;
    const nodes = containerEl.querySelectorAll('[data-listing-reorder-id]');
    const n = nodes.length;
    if (n === 0) return 0;
    for (let i = 0; i < n; i += 1) {
        const rect = nodes[i].getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        if (clientY < mid) return i;
    }
    return n;
}

/** Search matches display label, technical field name, and listing_fields_id; all words must match (AND). */
function fieldMatchesSearch(field, rawQuery) {
    const q = String(rawQuery || '').trim().toLowerCase();
    if (!q) return true;
    const haystack = [
        getFieldLabel(field),
        field?.field_name ?? '',
        String(field?.listing_fields_id ?? ''),
    ]
        .join(' ')
        .toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    return tokens.every((t) => haystack.includes(t));
}

export default function ListingLayoutConsumer() {
    const navigate = useNavigate();
    const [activeHeader, setActiveHeader] = useState('show');
    const [activeTab, setActiveTab] = useState('active');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    /** From API: `response.filter.present.field_array` — shown under Active Fields */
    const [activeFieldList, setActiveFieldList] = useState([]);
    /** From API: `response.filter.present.field_list` — shown under Inactive Fields */
    const [inactiveFieldList, setInactiveFieldList] = useState([]);
    const [isMobile, setIsMobile] = useState(false);
    const [gearMenuOpen, setGearMenuOpen] = useState(false);
    const [gearSpinning, setGearSpinning] = useState(false);
    const gearMenuRef = useRef(null);
    const [areaUnitsModalOpen, setAreaUnitsModalOpen] = useState(false);
    /** 'sqft' | 'sqm' — chosen in Area units modal */
    const [areaUnitChoice, setAreaUnitChoice] = useState('sqft');
    /** Active field `listing_fields_id` values (synced from API; drives comma `selectto` for add_info_card_field) */
    const [, setActiveListIds] = useState([]);
    const [addFieldPending, setAddFieldPending] = useState(false);
    /** While dragging an active field (listing_fields_id string) */
    const [draggingActiveFieldId, setDraggingActiveFieldId] = useState(null);
    /** Drop line: insert before this index in `activeFieldList` (0..length); null when not dragging over list */
    const [dragOverInsertBeforeIndex, setDragOverInsertBeforeIndex] = useState(null);
    /** Ref sync for drag — state updates can lag; container dragOver needs this for preventDefault */
    const draggingFieldRef = useRef(null);
    const listingCardsContainerRef = useRef(null);
    const reorderByInsertBeforeIndexRef = useRef(() => {});
    const [userId, setUserId] = useState(null);
    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        setUserId(userid);
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleGearClickOutside = (e) => {
            if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) {
                setGearMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleGearClickOutside);
        return () => document.removeEventListener('mousedown', handleGearClickOutside);
    }, []);

    useEffect(() => {
        if (!areaUnitsModalOpen) return;
        const onKeyDown = (e) => {
            if (e.key === 'Escape') setAreaUnitsModalOpen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [areaUnitsModalOpen]);

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        fetchData(userid);
    }, []);

    function fetchData(userid, { silent = false } = {}) {
        if (!userid) {
            setLoading(false);
            setLoadError('Please sign in again.');
            return Promise.resolve();
        }
        if (!silent) {
            setLoading(true);
        }
        setLoadError('');
        const formdata = new FormData();
        formdata.append('user_id', userid);
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
        return fetch(`${baseURL.URL}/portfolio/consumer_infocards`, {
            method: 'POST',
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                const ok = isServerSuccess(responseJson);
                if (!ok) {
                    setLoadError(responseJson?.server?.message || 'Could not load info cards.');
                    setActiveFieldList([]);
                    setInactiveFieldList([]);
                    setActiveListIds([]);
                    setLoading(false);
                    return;
                }
                const present = responseJson?.response?.filter?.present;
                const activeArray = Array.isArray(present?.field_array) ? present.field_array : [];
                const inactiveArray = Array.isArray(present?.field_list) ? present.field_list : [];
                setActiveFieldList(activeArray);
                setInactiveFieldList(inactiveArray);
                const ids = activeArray
                    .map((f) => f?.listing_fields_id)
                    .filter((x) => x != null && x !== '')
                    .map(String);
                setActiveListIds(ids);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoadError('Something went wrong while loading fields.');
                setActiveFieldList([]);
                setInactiveFieldList([]);
                setActiveListIds([]);
                setLoading(false);
            });
    }

    const totalFieldCount = activeTab === 'active' ? activeFieldList.length : inactiveFieldList.length;

    const currentFields = useMemo(() => {
        const base = activeTab === 'active' ? activeFieldList : inactiveFieldList;
        return base.filter((f) => fieldMatchesSearch(f, searchQuery));
    }, [activeTab, activeFieldList, inactiveFieldList, searchQuery]);


    function addFieldApi(selectToIds) {
        const userid = localStorage.getItem('user_id');
        if (!userid || selectToIds == null) {
            return;
        }
        setAddFieldPending(true);
        const formdata = new FormData();
        formdata.append('user_id', userid);
        formdata.append('selectto', String(selectToIds));
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');

        fetch(`${baseURL.URL}/portfolio/add_consumer_info_card_field`, {
            method: 'POST',
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                if (isServerSuccess(responseJson)) {
                    const msg = responseJson?.server?.message || 'Listing fields updated.';
                    showNotification('Success', msg, 'success');
                    return fetchData(userid, { silent: true });
                }
                const errMsg = responseJson?.server?.message || 'Could not update listing fields.';
                showNotification('Error', errMsg, 'danger');
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', error?.message || 'Something went wrong.', 'danger');
            })
            .finally(() => {
                setAddFieldPending(false);
            });
    }

    function toggleToActive(toggleId) {
        const idStr = String(toggleId);
        setActiveListIds((prev) => {
            if (prev.includes(idStr)) {
                return prev;
            }
            const next = [...prev, idStr];
            const selectToIds = next.join(',');
            queueMicrotask(() => addFieldApi(selectToIds));
            return next;
        });
    }

    function toggleToInactive(toggleId) {
        const idStr = String(toggleId);
        setActiveTab('active');
        setActiveListIds((prev) => {
            const next = prev.filter((x) => x !== idStr);
            const selectToIds = next.join(',');
            queueMicrotask(() => addFieldApi(selectToIds));
            return next;
        });
    }

    /** Reorder by final insert-before index (0..prev.length) in the list before the move. */
    function reorderActiveFieldsToInsertBefore(dragId, insertBeforeIndex) {
        setActiveFieldList((prev) => {
            const fromIdx = prev.findIndex((f) => String(f?.listing_fields_id) === String(dragId));
            if (fromIdx < 0) return prev;
            const bounded = Math.max(0, Math.min(insertBeforeIndex, prev.length));
            if (bounded === fromIdx) return prev;
            const next = [...prev];
            const [removed] = next.splice(fromIdx, 1);
            let insertAt = bounded;
            if (fromIdx < bounded) {
                insertAt = bounded - 1;
            }
            next.splice(insertAt, 0, removed);
            const ids = next
                .map((f) => f?.listing_fields_id)
                .filter((x) => x != null && x !== '')
                .map(String);
            const selectToIds = ids.join(',');
            queueMicrotask(() => {
                setActiveListIds(ids);
                addFieldApi(selectToIds);
            });
            return next;
        });
    }

    reorderByInsertBeforeIndexRef.current = reorderActiveFieldsToInsertBefore;

    useEffect(() => {
        if (loading) return undefined;
        const el = listingCardsContainerRef.current;
        if (!el) return undefined;

        const onDragOverCapture = (e) => {
            if (!draggingFieldRef.current) return;
            if (!el.contains(e.target)) return;
            if (!el.querySelector('[data-listing-reorder-id]')) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'move';
            const insertIdx = getReorderInsertBeforeIndex(el, e.clientY);
            setDragOverInsertBeforeIndex(insertIdx);
        };

        const onDropCapture = (e) => {
            if (!draggingFieldRef.current) return;
            if (!el.contains(e.target)) return;
            if (!el.querySelector('[data-listing-reorder-id]')) return;
            e.preventDefault();
            e.stopPropagation();
            const raw =
                e.dataTransfer.getData('text/plain') ||
                e.dataTransfer.getData('application/x-listing-field-id');
            const insertIdx = getReorderInsertBeforeIndex(el, e.clientY);
            draggingFieldRef.current = null;
            setDraggingActiveFieldId(null);
            setDragOverInsertBeforeIndex(null);
            if (raw) {
                reorderByInsertBeforeIndexRef.current(raw, insertIdx);
            }
        };

        el.addEventListener('dragover', onDragOverCapture, true);
        el.addEventListener('drop', onDropCapture, true);
        return () => {
            el.removeEventListener('dragover', onDragOverCapture, true);
            el.removeEventListener('drop', onDropCapture, true);
        };
    }, [loading]);

    useEffect(() => {
        draggingFieldRef.current = null;
        setDraggingActiveFieldId(null);
        setDragOverInsertBeforeIndex(null);
    }, [activeTab]);

    /** Mobile: move one active field up/down in the full list order. */
    function moveActiveField(listingFieldId, direction) {
        setActiveFieldList((prev) => {
            const currentIndex = prev.findIndex(
                (f) => String(f?.listing_fields_id) === String(listingFieldId)
            );
            if (currentIndex < 0) return prev;
            const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
            if (swapIndex < 0 || swapIndex >= prev.length) return prev;
            const next = [...prev];
            [next[currentIndex], next[swapIndex]] = [next[swapIndex], next[currentIndex]];
            const ids = next
                .map((f) => f?.listing_fields_id)
                .filter((x) => x != null && x !== '')
                .map(String);
            const selectToIds = ids.join(',');
            queueMicrotask(() => {
                setActiveListIds(ids);
                addFieldApi(selectToIds);
            });
            return next;
        });
    }

    /** `unit_preference`: 'sqft' | 'sqm' — sent to `/member/set_unit_preference` */
    function saveUnitPreference(unitPreference) {
        const userid = localStorage.getItem('user_id');
        if (!userid) {
            showNotification('Error', 'Please sign in again.', 'danger');
            return;
        }
        const formdata = new FormData();
        formdata.append('user_id', userid);
        formdata.append('unit_preference', String(unitPreference));
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
        fetch(`${baseURL.URL}/member/set_unit_preference`, {
            method: 'POST',
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                if (isServerSuccess(responseJson)) {
                    setAreaUnitsModalOpen(false);
                    showNotification('Success', responseJson?.server?.message || 'Unit preference saved.', 'success');
                } else {
                    showNotification('Error', responseJson?.server?.message || 'Could not save unit preference.', 'danger');
                }
            })
            .catch((error) => {
                console.error(error);
                showNotification('Error', error?.message || 'Something went wrong.', 'danger');
            });
    }

    return (
        <div className="listing-layout-page">
            <div className="container">
                {isMobile && (
                    <div className="mobile-page-header">
                        <button
                            type="button"
                            className="mobile-back-btn"
                            onClick={() => navigate(-1)}
                            aria-label="Back"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M15 18L9 12L15 6"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        <h2 className="mobile-page-title">{stringsoflanguages.consumerListingLayout}</h2>
                    </div>
                )}
                <div className="search-filter-section">
                    <div className="search-bar" role="search">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            className="search-icon"
                        >
                            <path
                                d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                                stroke="#E1730B"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M17.5 17.5L13.875 13.875"
                                stroke="#E1730B"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        <input
                            type="search"
                            placeholder="Search by field name or label"
                            className="search-input"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            aria-label="Search fields by name or label"
                            autoComplete="off"
                        />
                    </div>
                    <button type="button" className="filter-btn" aria-label="Filter">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <g clipPath="url(#clip0_636_6322)">
                                <path d="M10.5659 12.0988L2.90233 7.9339C2.56145 7.74874 2.15891 7.75638 1.82534 7.95467C1.49177 8.15292 1.29272 8.50295 1.29272 8.891V18.5526C1.29272 18.9435 1.50382 19.3064 1.84367 19.4996L9.50737 23.8564C9.67631 23.9524 9.86105 24.0004 10.0458 24.0004C10.2341 24.0004 10.4224 23.9506 10.5936 23.8509C10.9327 23.6537 11.1351 23.3017 11.1351 22.9093V13.0559C11.1351 12.6564 10.917 12.2896 10.5659 12.0988Z" fill="white" />
                                <path d="M21.317 5.42039C21.3165 5.01246 21.0911 4.64245 20.729 4.45476L12.3721 0.122251C12.0557 -0.0418609 11.6786 -0.0406418 11.3634 0.125299L3.1294 4.45781C2.77088 4.64653 2.54788 5.01541 2.54736 5.42058C2.5469 5.82565 2.76901 6.19505 3.12701 6.38457L11.361 10.7441C11.5204 10.8285 11.6955 10.8707 11.8706 10.8707C12.0436 10.8707 12.2166 10.8295 12.3744 10.7471L20.7315 6.38767C21.0932 6.19899 21.3175 5.82833 21.317 5.42039Z" fill="white" />
                                <path d="M22.1747 7.95426C21.8413 7.75597 21.4386 7.74814 21.0979 7.93345L13.4341 12.0984C13.083 12.2892 12.865 12.6559 12.865 13.0555V22.9089C12.865 23.3012 13.0674 23.6532 13.4065 23.8505C13.5777 23.9501 13.7659 23.9999 13.9542 23.9999C14.139 23.9999 14.3238 23.9519 14.4927 23.856L22.1564 19.4992C22.4963 19.3059 22.7073 18.9431 22.7073 18.5521V8.89059C22.7073 8.50254 22.5083 8.15251 22.1747 7.95426Z" fill="white" />
                            </g>
                            <defs>
                                <clipPath id="clip0_636_6322">
                                    <rect width="24" height="24" fill="white" />
                                </clipPath>
                            </defs>
                        </svg>
                    </button>
                </div>
                <div className="listing-layout-content">
                    <div className="listing-header-options">
                        <button
                            type="button"
                            className={`header-option `}
                            onClick={() => setActiveHeader('show')}
                        >
                            <input id="field-active" className="listing-radio" type="radio" checked name="listing-active-optional"></input>
                            {/* <span className={`option-indicator ${activeHeader === 'show' ? 'active' : ''}`} /> */}
                            {stringsoflanguages.show}
                        </button>
                        <button
                            type="button"
                            className={`header-option `}
                            onClick={() => setActiveHeader('hide')}
                        >
                            <input id="field-active" className="listing-radio" type="radio" name="listing-active-optional"></input>
                            {/* <span className={`option-indicator ${activeHeader === 'hide' ? 'active' : ''}`} /> */}
                            {stringsoflanguages.hide}
                        </button>
                        <button
                            type="button"
                            className={`header-option `}
                            onClick={() => setActiveHeader('position')}
                        >
                            <i class="fa-solid fa-grip-vertical" aria-hidden="true"></i>
                            {stringsoflanguages.position}
                        </button>
                    </div>

                    <div className="listing-tab-selection">
                        <button
                            type="button"
                            className={`listing-tab ${activeTab === 'active' ? 'active' : ''}`}
                            onClick={() => setActiveTab('active')}
                        >
                            {stringsoflanguages.activeFields}
                        </button>
                        <button
                            type="button"
                            className={`listing-tab ${activeTab === 'inactive' ? 'active' : ''}`}
                            onClick={() => setActiveTab('inactive')}
                        >
                            {stringsoflanguages.inactiveFields}
                        </button>
                    </div>

                    {!loading && !loadError && searchQuery.trim() !== '' && (
                        <p className="listing-search-results-hint" aria-live="polite">
                            {`Showing ${currentFields.length} of ${totalFieldCount} field${totalFieldCount !== 1 ? 's' : ''
                                } for "${searchQuery.trim()}"`}
                        </p>
                    )}

                    {loadError && (
                        <p className="no-data-message" style={{ marginTop: 12 }}>
                            {loadError}
                        </p>
                    )}
                    {loading ? (
                        <p className="no-data-message" style={{ marginTop: 12 }}>
                            {stringsoflanguages.loading} fields…
                        </p>
                    ) : (
                        <div ref={listingCardsContainerRef} className="listing-cards-container">
                            {currentFields.length === 0 ? (
                                <p className="no-data-message">
                                    {searchQuery.trim()
                                        ? 'No fields match your search.'
                                        : activeTab === 'active'
                                            ? 'No active fields from the server.'
                                            : 'No inactive fields from the server.'}
                                </p>
                            ) : (
                                currentFields.map((field, index) => {
                                    const id = field?.listing_fields_id ?? `idx-${index}`;
                                    const key = `${activeTab}-${id}`;
                                    const locked = isFieldLocked(field);

                                    if (activeTab === 'inactive') {
                                        const listingFieldsId = field?.listing_fields_id;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                className="listing-card listing-card--inactive-add"
                                                disabled={addFieldPending || listingFieldsId == null}
                                                onClick={() => toggleToActive(listingFieldsId)}
                                                aria-label={`Add ${getFieldLabel(field)} to active fields`}
                                            >
                                                <span className="listing-field-lock" aria-hidden="true">
                                                    <i className="fa-solid fa-circle-plus" />
                                                </span>
                                                <span className="listing-card-label">{getFieldLabel(field)}</span>
                                            </button>
                                        );
                                    }

                                    const listingFieldsId = field?.listing_fields_id;
                                    const canDragReorder =
                                        activeTab === 'active' &&
                                        !addFieldPending &&
                                        searchQuery.trim() === '' &&
                                        listingFieldsId != null;
                                    const rowIndex =
                                        canDragReorder
                                            ? activeFieldList.findIndex(
                                                (f) => String(f?.listing_fields_id) === String(listingFieldsId)
                                            )
                                            : -1;
                                    const activeCount = activeFieldList.length;
                                    const isDragOver =
                                        canDragReorder &&
                                        dragOverInsertBeforeIndex != null &&
                                        (dragOverInsertBeforeIndex === rowIndex ||
                                            (dragOverInsertBeforeIndex === activeCount &&
                                                rowIndex === activeCount - 1));

                                    return (
                                        <div
                                            key={key}
                                            data-listing-reorder-id={canDragReorder ? String(listingFieldsId) : undefined}
                                            className={`listing-card ${locked ? 'listing-card--required' : 'listing-card--active-remove'}${canDragReorder ? ' listing-card--active-remove-row' : ''}${isDragOver ? ' listing-card--drag-over' : ''}${draggingActiveFieldId != null && String(draggingActiveFieldId) === String(listingFieldsId) ? ' listing-card--dragging' : ''}`}
                                            title={
                                                locked
                                                    ? isFieldDefault(field)
                                                        ? 'Default field — drag to reorder'
                                                        : 'Required field — drag to reorder'
                                                    : undefined
                                            }
                                        >
                                            {canDragReorder && (
                                                <div
                                                    className="listing-card-drag-handle"
                                                    draggable={true}
                                                    onDragStart={(e) => {
                                                        if (listingFieldsId == null) return;
                                                        e.stopPropagation();
                                                        const idStr = String(listingFieldsId);
                                                        draggingFieldRef.current = idStr;
                                                        e.dataTransfer.setData('text/plain', idStr);
                                                        e.dataTransfer.setData('application/x-listing-field-id', idStr);
                                                        e.dataTransfer.effectAllowed = 'move';
                                                        setDraggingActiveFieldId(idStr);
                                                    }}
                                                    onDragEnd={() => {
                                                        draggingFieldRef.current = null;
                                                        setDraggingActiveFieldId(null);
                                                        setDragOverInsertBeforeIndex(null);
                                                    }}
                                                    aria-label={`Drag to reorder ${getFieldLabel(field)}`}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
                                                    }}
                                                >
                                                    <i className="fa-solid fa-grip-vertical" aria-hidden="true" />
                                                </div>
                                            )}
                                            {locked ? (
                                                <>
                                                    <span className="listing-field-lock" aria-hidden="true">
                                                        <i className="fa-solid fa-lock" />
                                                    </span>
                                                    <span className="listing-card-label listing-card-label--locked">
                                                        {getFieldLabel(field)}
                                                    </span>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="listing-card-remove-main"
                                                    disabled={addFieldPending || listingFieldsId == null}
                                                    onClick={() => toggleToInactive(listingFieldsId)}
                                                    aria-label={`Remove ${getFieldLabel(field)} from active fields`}
                                                    onDragOver={
                                                        canDragReorder
                                                            ? (e) => {
                                                                e.preventDefault();
                                                                e.dataTransfer.dropEffect = 'move';
                                                            }
                                                            : undefined
                                                    }
                                                >
                                                    <span className="listing-field-lock" aria-hidden="true">
                                                        <i className="fa-solid fa-circle-minus" />
                                                    </span>
                                                    <span className="listing-card-label">{getFieldLabel(field)}</span>
                                                </button>
                                            )}
                                            {isMobile && canDragReorder && (
                                                <div
                                                    className="listing-card-mobile-reorder"
                                                    style={{ display: 'flex', gap: 8, marginLeft: 8 }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="listing-card-mobile-reorder-btn"
                                                        disabled={addFieldPending || rowIndex <= 0}
                                                        onClick={() => moveActiveField(listingFieldsId, 'up')}
                                                        aria-label={`Move ${getFieldLabel(field)} up`}
                                                        title="Move up"
                                                        style={{ minWidth: 28, minHeight: 28 }}
                                                    >
                                                        <i className="fa-solid fa-chevron-up" aria-hidden="true" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="listing-card-mobile-reorder-btn"
                                                        disabled={
                                                            addFieldPending ||
                                                            rowIndex < 0 ||
                                                            rowIndex >= activeCount - 1
                                                        }
                                                        onClick={() => moveActiveField(listingFieldsId, 'down')}
                                                        aria-label={`Move ${getFieldLabel(field)} down`}
                                                        title="Move down"
                                                        style={{ minWidth: 28, minHeight: 28 }}
                                                    >
                                                        <i className="fa-solid fa-chevron-down" aria-hidden="true" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
                {!isMobile && (
                    <div className="btn-section btn-section-gear" ref={gearMenuRef}>
                        <button
                            type="button"
                            className={`btn-primary gear-btn ${gearSpinning ? 'gear-spin' : ''}`}
                            onClick={() => {
                                setGearMenuOpen((prev) => !prev);
                                setGearSpinning(true);
                                setTimeout(() => setGearSpinning(false), 500);
                            }}
                            aria-label="Options"
                            aria-expanded={gearMenuOpen}
                        >
                            <i className="fa-solid fa-gear"></i>
                        </button>
                        {gearMenuOpen && (
                            <ul className="gear-dropdown" aria-label="Options list">
                                <li>
                                    <button
                                        type="button"
                                        className="gear-dropdown-item"
                                        onClick={() => {
                                            setGearMenuOpen(false);
                                            setAreaUnitsModalOpen(true);
                                        }}
                                    >
                                        <i className="fa-solid fa-arrows-up-down-left-right" aria-hidden="true" />
                                        <span>Area units</span>
                                    </button>
                                </li>
                            </ul>
                        )}
                    </div>
                )}
                {/* Mobile: bottom nav with 6 icons from image */}
                {isMobile && (
                    <nav className="mobile-bottom-nav liquidGlass-wrapper dock members-profile-footer-nav" aria-label="Profile actions">
                        <div className="liquidGlass-effect" aria-hidden="true" />
                        <div className="liquidGlass-shine" aria-hidden="true" />

                        <>

                            <div
                                className="mobile-nav-item"
                                role="button"
                                tabIndex={0}
                                aria-label="Area units"
                                onClick={() => setAreaUnitsModalOpen(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setAreaUnitsModalOpen(true);
                                    }
                                }}
                            >
                                <i className="fa-solid fa-arrows-up-down-left-right" aria-hidden="true" />
                            </div>
                        </>

                    </nav>
                )}
            </div>

            {areaUnitsModalOpen && (
                <div
                    className="modal-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="area-units-modal-title"
                    onClick={() => setAreaUnitsModalOpen(false)}
                >
                    <div className="logout-modal portfolio-folder-modal" onClick={(e) => e.stopPropagation()}>
                        <h2 id="area-units-modal-title" className="text-white mb-4 text-center">
                            Area units
                        </h2>
                        <div className="portfolio-folder-modal-options">
                            <button
                                type="button"
                                className={`folder-modal-option-btn ${areaUnitChoice === 'Sq Feet' ? 'listing-area-unit-btn--selected' : ''}`}
                                onClick={() => {
                                    setAreaUnitChoice('Sq Feet');
                                    saveUnitPreference('Sq Feet');
                                }}
                            >
                                Sq Feet
                            </button>
                            <button
                                type="button"
                                className={`folder-modal-option-btn ${areaUnitChoice === 'Sq Meter' ? 'listing-area-unit-btn--selected' : ''}`}
                                onClick={() => {
                                    setAreaUnitChoice('Sq Meter');
                                    saveUnitPreference('Sq Meter');
                                }}
                            >
                                Sq Meter
                            </button>
                        </div>
                        <button type="button" className="save-btn listing-area-units-ok-btn" onClick={() => setAreaUnitsModalOpen(false)}>
                            OK
                        </button>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
